import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { createInteractingObservable } from '@livekit/components-core'

import SvgChevron from '@/src/assets/icons/Chevron'

import type { usePagination } from '@/src/hooks'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

export interface PaginationControlProps
  extends Pick<
    ReturnType<typeof usePagination>,
    'totalPageCount' | 'nextPage' | 'prevPage' | 'currentPage'
  > {
  /** Reference to an HTML element that holds the pages, while interacting (`mouseover`)
   *  with it, the pagination controls will appear for a while. */
  pagesContainer?: React.RefObject<HTMLElement>
}

export function PaginationControl({
  totalPageCount,
  nextPage,
  prevPage,
  currentPage,
  pagesContainer: connectedElement,
}: PaginationControlProps) {
  const { t } = useTranslation()
  const [interactive, setInteractive] = useState(false)
  const { primary_color } = useAppSelector(selectBranding)

  useEffect(() => {
    let subscription:
      | ReturnType<ReturnType<typeof createInteractingObservable>['subscribe']>
      | undefined
    if (connectedElement) {
      subscription = createInteractingObservable(connectedElement.current, 2000).subscribe(
        setInteractive,
      )
    }
    return () => {
      if (subscription) {
        subscription?.unsubscribe()
      }
    }
  }, [connectedElement])

  return (
    <div className="lk-pagination-control" data-lk-user-interaction={interactive}>
      <button className="lk-button" onClick={prevPage}>
        <SvgChevron />
      </button>

      <span
        className="lk-pagination-count"
        style={{
          color: primary_color,
        }}
      >{`${currentPage} ${t('lkPaginationCount')} ${totalPageCount}`}</span>

      <button className="lk-button" onClick={nextPage}>
        <SvgChevron />
      </button>
    </div>
  )
}
